/**
 * createVideoPlane() — VideoTexture plane for camera feed rendering.
 *
 * Creates a PlaneGeometry sized to exactly fill the camera frustum at the
 * specified depth, textured with a VideoTexture from the given HTML video
 * element. Used by the eyewear VTO to render the webcam feed and 3D
 * glasses overlay on the same canvas.
 *
 * The plane is configured for correct compositing:
 *   - depthWrite: false (glasses at slightly shallower depth are never clipped)
 *   - renderOrder: -1 (drawn behind everything)
 *   - toneMapped: false (camera feed keeps natural colors)
 *
 * Usage:
 *   const THREE = await import('three');
 *   const videoPlane = createVideoPlane(THREE, videoElement, {
 *     fov: 63,
 *     aspect: 4/3,
 *     depth: 500,
 *   });
 *   scene.add(videoPlane);
 */

import type { Mesh } from 'three';
import type { ThreeModule, VideoPlaneConfig } from './types.js';

/**
 * Create a camera-aligned video texture plane.
 *
 * @param THREE        - Dynamically imported Three.js module.
 * @param videoElement - HTML video element to use as the texture source.
 * @param config       - FOV, aspect ratio, and depth configuration.
 * @returns A Mesh with VideoTexture, positioned at -depth on the Z axis.
 */
export function createVideoPlane(
  THREE: ThreeModule,
  videoElement: HTMLVideoElement,
  config: VideoPlaneConfig,
): Mesh {
  const { fov, aspect, depth } = config;

  const videoTex = new THREE.VideoTexture(videoElement);
  (videoTex as unknown as { colorSpace: string }).colorSpace =
    THREE.SRGBColorSpace as unknown as string;

  // Size the plane to exactly fill the camera frustum at the given depth
  const halfH = depth * Math.tan(THREE.MathUtils.degToRad(fov * 0.5));
  const halfW = halfH * aspect;

  const planeGeom = new THREE.PlaneGeometry(halfW * 2, halfH * 2);
  const planeMat = new THREE.MeshBasicMaterial({ map: videoTex });

  // Disable tone mapping so the camera feed appears with natural colors
  // (ACESFilmic would wash it out)
  (planeMat as unknown as { toneMapped: boolean }).toneMapped = false;

  // Disable depth write so 3D objects at slightly shallower depth
  // are never depth-clipped by the video plane
  planeMat.depthWrite = false;

  const videoPlane = new THREE.Mesh(planeGeom, planeMat) as Mesh;
  (videoPlane as unknown as { position: { set: (x: number, y: number, z: number) => void } })
    .position.set(0, 0, -depth);

  // Draw behind everything
  videoPlane.renderOrder = -1;

  return videoPlane;
}
